import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../screens/HomeView.vue'
import LocalCopiesView from '../screens/LocalCopiesView.vue'
import { useAuthStore } from '../stores/auth'
import { useConcentrationModeStore } from '../stores/concentrationMode'
import { normalizeProjectTab } from '../constants/projectTabQuery'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        userTypes: ['non-authenticated'],
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../screens/LoginView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest'],
      },
    },
    {
      path: '/password-recovery',
      name: 'password-recovery',
      component: () => import('../screens/PasswordRecoveryView.vue'),
      props: (route) => ({
        token: route.query.token,
      }),
      meta: {
        userTypes: ['non-authenticated', 'guest'],
      },
    },
    {
      path: '/email-verification',
      name: 'email-verification',
      component: () => import('../screens/EmailVerificationView.vue'),
      props: (route) => ({
        token: route.query.token,
      }),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
      },
    },
    {
      path: '/confirm-email',
      name: 'confirm-email',
      component: () => import('../screens/ConfirmEmailView.vue'),
      meta: {
        userTypes: ['registered'],
      },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../screens/ProfileView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'community',
      },
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('../screens/AccountView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
      },
    },
    {
      path: '/users/:publicAlias',
      name: 'public-user-profile',
      component: () => import('../screens/PublicUserProfileView.vue'),
      props: (route) => ({
        publicAlias: route.params.publicAlias,
      }),
      meta: {
        requiresOnline: true,
        userTypes: ['non-authenticated', 'guest', 'registered'],
        domain: 'community',
      },
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => import('../screens/ProjectsView.vue'),
      meta: {
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/sagas',
      name: 'sagas',
      component: () => import('../screens/SagasView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/saga/:sagaId',
      name: 'saga',
      component: () => import('../screens/SagaView.vue'),
      props: (route) => ({
        sagaId: route.params.sagaId,
      }),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/trash',
      name: 'trash',
      component: () => import('../screens/TrashView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/read/:publicId',
      name: 'read-snapshot',
      component: () => import('../screens/ReadSnapshotView.vue'),
      props: (route) => ({
        publicId: route.params.publicId,
      }),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
        domain: 'read',
      },
    },
    {
      path: '/project/:projectId',
      name: 'project',
      component: () => import('../screens/ProjectView.vue'),
      props: (route) => {
        const rawTab = route.query.tab || 'editor'
        // Added for plot and tools tab LEGACY support, remove after some time
        const tab = rawTab === 'plot' || rawTab === 'tools' ? 'planning' : rawTab

        return {
          projectId: route.params.projectId,
          tab,
          chapterId: route.query.chapterId || '',
          detail: route.query.detail || '',
        }
      },
      meta: {
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/local-copies',
      name: 'local-copies',
      component: LocalCopiesView,
      meta: {
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/images',
      name: 'images',
      component: () => import('../screens/ImagesView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
      },
    },
    {
      path: '/layouts',
      name: 'layouts',
      component: () => import('../screens/LayoutsView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/layouts/:layoutId',
      name: 'layout-edit',
      component: () => import('../screens/LayoutEditView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['registered'],
        domain: 'write',
      },
    },
    {
      path: '/progress',
      name: 'progress',
      component: () => import('../screens/ProgressView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/writing-sessions',
      name: 'writing-sessions',
      component: () => import('../screens/WritingSessionsView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/challenges',
      name: 'challenges',
      component: () => import('../screens/ChallengesView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/writing-feedback',
      name: 'writing-feedback',
      component: () => import('../screens/WritingFeedbackView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'write',
      },
    },
    {
      path: '/sessions',
      redirect: { path: '/account', query: { section: 'devices' } },
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('../screens/NotificationsView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
      },
    },
    {
      path: '/config',
      name: 'config',
      component: () => import('../screens/ConfigView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
      },
    },
    {
      path: '/community',
      redirect: (to) => {
        const rawTab = to.query.tab
        const tab = typeof rawTab === 'string' ? rawTab : ''
        const tabToPath: Record<string, string> = {
          activity: '/community/activity',
          groups: '/community/groups',
          capsule: '/community/capsule',
          leaderboard: '/community/activity',
        }
        const { tab: _removedTab, ...restQuery } = to.query
        return {
          path: tabToPath[tab] || '/community/activity',
          query: restQuery,
          hash: to.hash,
        }
      },
    },
    {
      path: '/community/activity',
      name: 'community-activity',
      component: () => import('../screens/CommunityActivityView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'community',
      },
    },
    {
      path: '/community/groups',
      name: 'community-groups',
      component: () => import('../screens/CommunityGroupsView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'community',
      },
    },
    {
      path: '/community/group/:groupId',
      name: 'community-group',
      component: () => import('../screens/CommunityGroupView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'community',
      },
    },
    {
      path: '/community/capsule',
      name: 'community-capsule',
      component: () => import('../screens/CommunityCapsuleView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['guest', 'registered'],
        domain: 'community',
      },
    },
    {
      path: '/how-it-works',
      name: 'how-it-works',
      component: () => import('../screens/HowItWorksView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
      },
    },
    {
      path: '/funcionalidades',
      name: 'features-catalog',
      component: () => import('../screens/FeaturesCatalogView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
        publicMarketing: true,
      },
    },
    {
      path: '/preguntas-frecuentes',
      name: 'faq',
      component: () => import('../screens/FaqView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
        publicMarketing: true,
      },
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('../screens/AdminView.vue'),
      meta: {
        requiresOnline: true,
        userTypes: ['registered'],
        requiresAdmin: true,
      },
    },
    {
      path: '/explorar/:slug',
      name: 'explore-feature',
      component: () => import('../screens/seo/ExploreFeatureView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
        publicMarketing: true,
      },
    },
    {
      path: '/blog',
      name: 'blog-index',
      component: () => import('../screens/seo/BlogIndexView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
        publicMarketing: true,
      },
    },
    {
      path: '/blog/:slug',
      name: 'blog-article',
      component: () => import('../screens/seo/BlogArticleView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
        publicMarketing: true,
      },
    },
    {
      path: '/terms-and-conditions',
      name: 'terms-and-conditions',
      component: () => import('../screens/TermsView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
      },
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: () => import('../screens/PricingView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
      },
    },
    {
      path: '/proximamente',
      name: 'proximamente',
      component: () => import('../screens/ComingSoonView.vue'),
      meta: {
        userTypes: ['non-authenticated', 'guest', 'registered'],
      },
    },
    {
      path: '/donations',
      name: 'donations',
      component: () => import('../screens/DonationsView.vue'),
      meta: {
        userTypes: ['guest', 'registered'],
      },
    },
  ],
})

// Navigation guard for route protection
function isSessionExitRoute(to: { name?: unknown; meta: { publicMarketing?: unknown } }): boolean {
  return (
    to.name === 'home' ||
    to.name === 'login' ||
    to.name === 'password-recovery' ||
    to.meta.publicMarketing === true
  )
}

router.beforeEach(async (to, from, next) => {
  const concentrationModeStore = useConcentrationModeStore()
  const authStore = useAuthStore()
  const sessionEnded = authStore.userType === 'non-authenticated'
  if (
    concentrationModeStore.mobileNavigationLock &&
    concentrationModeStore.active &&
    !sessionEnded &&
    !isSessionExitRoute(to) &&
    from.name === 'project' &&
    (to.name !== 'project' ||
      String(to.params.projectId || '') !== String(from.params.projectId || '') ||
      normalizeProjectTab(to.query.tab) !== 'editor')
  ) {
    next(false)
    return
  }

  const currentUserType = authStore.userType
  const allowedUserTypes = to.meta.userTypes as string[]

  if (to.meta.requiresAdmin) {
    await authStore.fetchSessionInfo()
    if (!authStore.isAdmin && !authStore.isImpersonating) {
      next({ name: 'projects' })
      return
    }
  }

  // Check if current user type is allowed for this route
  if (allowedUserTypes && !allowedUserTypes.includes(currentUserType)) {
    // User type not allowed for this route -> redirect based on user type
    if (currentUserType === 'non-authenticated') {
      // Non-authenticated user trying to access protected route -> redirect to home
      if (to.name !== 'home') {
        next({ name: 'home' })
        return
      }
    } else {
      // Authenticated user (guest or registered) trying to access non-authenticated route -> redirect to projects
      if (to.name !== 'projects') {
        next({ name: 'projects' })
        return
      }
    }
  }

  // Allow navigation
  next()
})

export default router
